"use strict";
/**
 * The PRODUCT cap on how many distinct materials one pattern or one project may use.
 *
 * ## This is a CONTENT rule. It is not, and must never become, a transaction-size rule.
 *
 * Two limits both happen to be the number 100 and they are not the same limit. Conflating them has
 * already cost this codebase one regression (BL-160) and one shipped ceiling (BL-122), so the
 * distinction is stated here, at the definition, rather than left to the call sites:
 *
 * | | `MAX_ENTITY_MATERIALS` (this file) | `MAX_TRANSACTION_ITEMS` (`lambda-utilities/services/material-paired-write.ts:133`) |
 * | --- | --- | --- |
 * | What it counts | distinct materials on one entity | DynamoDB items in one `TransactWriteItems` call |
 * | Who it is for | the user | the writer |
 * | On breach | REFUSE the publish, with an actionable message | CHUNK the write, silently |
 * | Where enforced | publish routes only | `requiresChunkedPath` at every material write path |
 *
 * **100 materials is roughly 204 items, not 100.** Each material costs a Link_Row *plus* its paired
 * Usage_Counter update (Req 3.4), so a first publish of a full-cap entity computes
 * `CORE_ITEMS_FIRST_PUBLISH + 2 x 100 = 204` items. A legal, at-the-cap publish therefore STILL takes
 * the Chunked_Path. Capping materials at 100 and then deleting chunking "because it now fits" would
 * break publish at ~47 materials — that is exactly BL-160.
 *
 * ## Why 100, and what a user does at the cap
 *
 * Operator decision, 2026-08-29. 100 covers essentially all real use; beyond it the author either
 * requests an increase or splits the surplus into a separate component that is not system-linked.
 * BL-122's craft research (advanced cross-stitch and embroidery legitimately reaching 30 to 90+
 * distinct materials) is the reason the number is 100 rather than something tighter — the cap has to
 * sit above the real ceiling of the craft, not below it.
 *
 * ## Where this is enforced, and deliberately where it is NOT
 *
 * ENFORCED at publish, before any write, on both entity types:
 *   - `services/lambda-secure/src/routes/publish-pattern.ts` (first publish)
 *   - `services/lambda-secure/src/routes/pattern-versioning.ts` (republish — the same user action,
 *     and an unguarded republish would let a 100-material pattern grow to 140)
 *   - `services/lambda-secure/src/routes/craft-room/projects/business-logic/publish-project.ts`
 *
 * NOT enforced on delete, archive, unarchive, unpublish, moderation takedown, version rollback,
 * appeal reinstatement (`routes/appeal-system.ts` calls `publishPatternVersion` directly) or
 * migration. Those are destructive or repair paths and must act on the REAL number, whatever it is. A
 * repair path that refused over-cap content would strand that content with no way out — which is the
 * BL-122 failure mode wearing a different hat.
 *
 * NOT enforced on creation or draft save. The editor prevents exceeding the cap at add time
 * (`services/website/src/components/shared/MaterialSelector.tsx`), and leaving drafts uncapped means
 * an over-cap draft can still be opened and trimmed rather than becoming unopenable.
 */
// Created: 2026-08-29
// Updated: 2026-08-29
Object.defineProperty(exports, "__esModule", { value: true });
exports.MATERIAL_CAP_ERROR_CODE = exports.MAX_ENTITY_MATERIALS = void 0;
exports.exceedsMaterialCap = exceedsMaterialCap;
exports.materialCapRefusalMessage = materialCapRefusalMessage;
exports.materialCapRefusal = materialCapRefusal;
exports.materialCapError = materialCapError;
exports.isMaterialCapExceededError = isMaterialCapExceededError;
/** The maximum number of distinct materials one pattern or project may use. */
exports.MAX_ENTITY_MATERIALS = 100;
/** `true` when a material count breaches the product cap. */
function exceedsMaterialCap(materialCount) {
    return materialCount > exports.MAX_ENTITY_MATERIALS;
}
/**
 * The user-facing refusal. Names the ACTUAL count and the exact number to remove.
 *
 * A bare "too many materials" is BL-122's anti-pattern: it tells the author that something is wrong
 * without telling them what to do, and it reads as a system fault rather than a content rule.
 */
function materialCapRefusalMessage(entity, materialCount) {
    const surplus = materialCount - exports.MAX_ENTITY_MATERIALS;
    return (`This ${entity} uses ${materialCount} materials. The maximum is ${exports.MAX_ENTITY_MATERIALS} — ` +
        `please remove ${surplus} ${surplus === 1 ? 'material' : 'materials'}.`);
}
/**
 * The single call-site shape: a refusal message, or `null` when the count is permitted.
 *
 * Returning message-or-null mirrors `validateAttachedPdfsScanned` in `publish-pattern.ts`, so the two
 * publish gates read the same way at every call site.
 */
function materialCapRefusal(entity, materialIds) {
    // DISTINCT ids. A duplicate id is one material and one Link_Row (`SK=material#{id}`), so counting
    // the raw array would refuse a publish that costs nothing extra. Both publish paths de-duplicate
    // before writing anyway.
    const count = new Set(materialIds ?? []).size;
    return exceedsMaterialCap(count) ? materialCapRefusalMessage(entity, count) : null;
}
/**
 * The `code` stamped on a cap refusal thrown as an `Error`.
 *
 * The project publish path is business logic that throws rather than a route that returns, and its
 * handler's catch-all answers 500. A cap breach is caller-actionable, so it must not land there —
 * `isMaterialCapExceededError` lets the handler map it to 400 without string-matching the message.
 */
exports.MATERIAL_CAP_ERROR_CODE = 'material_cap_exceeded';
/** Build the throwable form of a cap refusal, tagged with `MATERIAL_CAP_ERROR_CODE`. */
function materialCapError(message) {
    return Object.assign(new Error(message), { code: exports.MATERIAL_CAP_ERROR_CODE });
}
/**
 * Duck-typed on `code`, not `instanceof` — the house convention for cross-package error matching
 * (see `material-paired-write.ts`'s `TransactionCanceledLike` for why `instanceof` is unreliable
 * across a monorepo that resolves more than one copy of a package).
 */
function isMaterialCapExceededError(error) {
    return (typeof error === 'object' &&
        error !== null &&
        error.code === exports.MATERIAL_CAP_ERROR_CODE);
}
